import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CatalogueFournisseurService, {
  ProduitCatalogue,
} from '../services/CatalogueFournisseurService';

const CatalogueFournisseur: React.FC = () => {
  const { fournisseur } = useParams<{ fournisseur: string }>();
  const navigate = useNavigate();
  const [produits, setProduits] = useState<ProduitCatalogue[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filtres
  const [categorie, setCategorie] = useState<string>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Catégories disponibles
  const [categories, setCategories] = useState<string[]>([]);

  const userId = 3; // TODO: Récupérer depuis contexte
  const tenantId = 2; // Sellerie Marine

  useEffect(() => {
    loadCategories();
    loadProduits();
  }, [fournisseur, categorie, search, page]);

  const loadCategories = async () => {
    try {
      const cats = await CatalogueFournisseurService.getCategories(fournisseur);
      setCategories(cats.categories);
    } catch (err: any) {
      console.error('Erreur chargement catégories:', err);
    }
  };

  const loadProduits = async () => {
    if (!fournisseur) return;

    try {
      setLoading(true);
      setError(null);
      const result = await CatalogueFournisseurService.getCatalogueProduits(
        fournisseur,
        {
          categorie: categorie || undefined,
          search: search || undefined,
          page,
          per_page: 50,
        }
      );
      setProduits(result.produits);
      setTotalPages(result.total_pages);
      setTotal(result.total);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur chargement produits');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async (produit: ProduitCatalogue) => {
    try {
      setLoading(true);
      setError(null);

      // Vérifier doublon
      const doublon = await CatalogueFournisseurService.verifierDoublon(
        userId,
        tenantId,
        produit.nom_commercial,
        produit.categorie_principale
      );

      if (doublon.existe) {
        setError(`Ce produit existe déjà dans votre catalogue : ${doublon.produit.nom}`);
        return;
      }

      // Importer avec remise (récupérer depuis user_fournisseurs)
      const result = await CatalogueFournisseurService.importerProduit({
        user_id: userId,
        tenant_id: tenantId,
        produit_id: produit.id,
        remise_appliquee: 15, // TODO: Récupérer remise depuis user_fournisseurs
      });

      setSuccess(
        `✅ Produit importé avec succès dans ${result.table} (prix avec remise: ${result.prix_applique.toFixed(2)} €)`
      );
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur import produit');
    } finally {
      setLoading(false);
    }
  };

  const getCategorieColor = (cat: string) => {
    switch (cat) {
      case 'Tissus':
        return 'bg-blue-100 text-blue-800';
      case 'Mousses':
        return 'bg-green-100 text-green-800';
      case 'Accessoires':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6">
      {/* En-tête */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <button
            onClick={() => navigate('/parametres')}
            className="text-blue-600 hover:text-blue-800 mb-2 flex items-center"
          >
            ← Retour aux paramètres
          </button>
          <h1 className="text-2xl font-semibold text-gray-900">
            Catalogue {fournisseur}
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            {total} produits disponibles
          </p>
        </div>
      </div>

      {/* Alertes */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
          {error}
          <button className="float-right" onClick={() => setError(null)}>
            ×
          </button>
        </div>
      )}

      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Filtres */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Catégorie
            </label>
            <select
              value={categorie}
              onChange={(e) => {
                setCategorie(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">Toutes les catégories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Recherche
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Nom ou référence..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setCategorie('');
                setSearch('');
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>

      {/* Liste produits */}
      {loading && produits.length === 0 ? (
        <div className="flex justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement...</p>
          </div>
        </div>
      ) : produits.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-600">Aucun produit trouvé</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {produits.map((produit) => (
              <div
                key={produit.id}
                className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow p-4"
              >
                <div className="flex justify-between items-start mb-2">
                  <span
                    className={`text-xs px-2 py-1 rounded ${getCategorieColor(
                      produit.categorie_principale
                    )}`}
                  >
                    {produit.categorie_principale}
                  </span>
                  <span className="text-xs text-gray-500">
                    Réf: {produit.reference}
                  </span>
                </div>

                <h3 className="font-medium text-gray-900 mb-2">
                  {produit.nom_commercial}
                </h3>

                <div className="text-sm text-gray-600 space-y-1 mb-4">
                  {produit.type_produit && (
                    <div>
                      <strong>Type:</strong> {produit.type_produit}
                    </div>
                  )}
                  {produit.couleurs_disponibles && Array.isArray(produit.couleurs_disponibles) && produit.couleurs_disponibles.length > 0 && (
                    <div className="flex items-center">
                      <strong>Couleurs:</strong> 
                      <span className="ml-1 px-2 py-0.5 bg-indigo-100 text-indigo-800 rounded-full text-xs font-medium">
                        {produit.couleurs_disponibles.length} {produit.couleurs_disponibles.length > 1 ? 'couleurs' : 'couleur'}
                      </span>
                    </div>
                  )}
                  {produit.laize && (
                    <div>
                      <strong>Laize:</strong> {produit.laize} cm
                    </div>
                  )}
                  {produit.composition && (
                    <div>
                      <strong>Composition:</strong> {produit.composition}
                    </div>
                  )}
                  {produit.prix_public_ht && (
                    <div className="text-lg font-bold text-blue-600 mt-2">
                      {produit.prix_public_ht.toFixed(2)} € HT / {produit.unite_prix}
                    </div>
                  )}
                </div>

                <button
                  onClick={() => handleImport(produit)}
                  disabled={loading}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 transition-colors text-sm"
                >
                  ➕ Importer dans mon catalogue
                </button>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center space-x-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-4 py-2 border rounded disabled:opacity-50"
              >
                Précédent
              </button>
              <span className="text-sm text-gray-600">
                Page {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 border rounded disabled:opacity-50"
              >
                Suivant
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CatalogueFournisseur;
